/**
 * Autonomous committees of the institute, as published at bietdvg.edu/committees
 * (fetched 2026-08-03). Order and membership are reproduced exactly as the source
 * accordion lists them — no regrouping, no invented entries.
 *
 * Two things about the source worth knowing:
 *  · Rosters here come from the live "Autonomous Committees" accordion. The page
 *    also carries older compositions labelled "for the year 2020-2021 / 2021-2022";
 *    those are historical and are deliberately NOT used.
 *  · `objectives` are the published "Roles and Responsibilities" bullets, which the
 *    source documents for only 13 committees. The Academics duties are attached to
 *    "Dean : Academics" (the current roster) rather than to the "Academics Committee"
 *    block, whose roster is one of those historical ones.
 *
 * Most committees publish a roster and nothing else, so `purpose` and `objectives`
 * are optional and members are plain names — the source states no per-member role,
 * and none is inferred here.
 *
 * Rendered as an expandable card grid at /compliance/committees.
 */

export interface AutonomousCommittee {
  slug: string;
  name: string;
  /** Dean portfolio the source page files this committee under. */
  category: string;
  /**
   * Blurb for the closed card — editorial, not published by the institute. The
   * source's own preamble (`purpose`) exists for only 12 committees and runs from
   * 156 to 635 characters, far too uneven to use here.
   *
   * Keep these to 150–200 characters. The card clamps the blurb to three lines, so
   * anything longer is silently truncated; this range fills two lines and most of a
   * third without running over.
   */
  summary: string;
  /** Published preamble, where the source has one. */
  purpose?: string;
  /** Published "Roles and Responsibilities" bullets, where the source has them. */
  objectives?: string[];
  /** Members as published — names only; the source states no per-member role. */
  team: string[];
}

export const AUTONOMOUS_COMMITTEES: AutonomousCommittee[] = [
  {
    slug: "finance-committee",
    name: "Finance Committee",
    category: "Finance & purchase",
    summary: "Provides the institute's financial oversight — examining the annual budget from departments, confirming expenditure carries budgetary provision, and recommending proposals referred by the Principal.",
    purpose: "The Institute has constituted, the finance committee, primarily to provide the financial oversight for the organization. The committee will discharge the functions as listed.",
    objectives: [
      "To ensure financial elements of Institute are in accordance with its vision, mission and strategic plan.",
      "To examine and scrutinize the annual budget submitted by departments.",
      "To ensure that expenses incurred have budgetary provision.",
      "To recommend approval of financial proposals referred by Principal/Director.",
      "To ensure that necessary formalities have been followed in incurring expenses in case of major purchase.",
      "To recommend any proposal for promotion, enhancement of DA, etc.",
    ],
    team: [
      "Dr. H.B. Aravind",
      "Prof. Y. Vrushabhendrappa",
      "Governing Body Nominee",
      "Dr.G P Desai",
      "Dr. C P Anila Kumar",
      "Dr. Sujith Kumar S H (MBA)",
      "Dr. Leela G H",
      "Dr. T N Vijaya Kumar",
      "Sri. T C Rajashekar",
      "Sri. Arun kumar K C",
      "Sri. Prashanth B",
    ],
  },
  {
    slug: "budget-approval-committee",
    name: "Budget Approval Committee",
    category: "Finance & purchase",
    summary: "Reviews the budget proposals departments put forward each year and approves them before funds are committed, so spending across the institute is planned and sanctioned in advance.",
    team: [
      "Finance Committee Members",
    ],
  },
  {
    slug: "purchase-committee",
    name: "Purchase Committee",
    category: "Finance & purchase",
    summary: "Monitors the purchase of equipment, consumables and other material the institute's programmes need, checking formalities are followed and that what is bought matches what departments asked for.",
    purpose: "The Institute constituted the purchasing committee to monitor the purchasing of equipment, consumables and other items necessary for smooth running of all program. The committee will discharge the functions as listed.",
    objectives: [
      "To identify and monitor the purchases required for the academic, administrative and amenities requirements for the institute.",
      "To ensure proportionality, transparency, accountability and fairness in the procurement process.",
      "To ensure that quality materials are procured and standard procedures are followed in major & minor purchases.",
      "To take indents from the departments/ sections/committees against requirement.",
      "To ensure concerned section/store in-charge makes the arrangements for getting quotations from reputed suppliers/company.",
      "To analyze and deliberate on the quotations and to recommend the company from which the items may be procured.",
      "To negotiate with suppliers in consultation with management for final approval.",
      "To ensure maintenance and service of items.",
    ],
    team: [
      "Finance Committee Members",
      "Concerned HOD",
      "Dr. Basavana Gowda S N",
    ],
  },
  {
    slug: "consultancy-co-ordinators",
    name: "Consultancy Co-ordinators",
    category: "Finance & purchase",
    summary: "Coordinate the consultancy work departments take on for industry and outside agencies, connecting faculty expertise to external problems alongside their teaching commitments.",
    team: [
      "Dr. S Suresh",
      "Dr. R S Chikkanagoudar",
    ],
  },
  {
    slug: "dean-academics",
    name: "Dean : Academics",
    category: "Academics",
    summary: "Frames the guidelines and regulations covering every academic affair of the institute, sets out perspective plans for its development, and proposes and reviews the academic calendar.",
    purpose: "The Institute constituted the Academic Committee to formulate long term and short-term development programmes of the Institute to achieve academic excellence in tune with the norms laid down by affiliating bodies. The committee will discharge the functions as listed.",
    objectives: [
      "To formulate the guidelines, rules & regulations of all academic affairs of the Institute.",
      "To visualize and formulate perspective plans for the development and growth of the Institute.",
      "To propose & review academic calendar as per need and directions of university.",
      "To review the academic and other related activities of the Institute.",
      "To review the faculty and student’s development programs.",
      "To draw and set schemes for the development of Institute.",
      "To plan for resource mobilization through writing project proposal to funding agencies, industry interaction, consultancy and extra-mural funding.",
      "To promote research and extension activities in the campus.",
      "To promote innovations in teaching and student placement program.",
      "To plan for sustaining the quality of education, quality improvement and accreditation of the Institute.",
      "To ensure and promote participation of departments in community-based activities in the region.",
      "To create a conducive environment for development of entrepreneurship.",
    ],
    team: [
      "Dr. Dinesh Y N",
      "Dr. Pradeep N",
      "Dr. Chidananda G",
    ],
  },
  {
    slug: "iqac",
    name: "IQAC",
    category: "Academics",
    summary: "Develops and applies quality benchmarks for academic and administrative work, acts as the institute's nodal agency for quality-related activity, and prepares the annual AQAR for NAAC.",
    purpose: "The IQAC plays a key role in improving and maintaining the quality of education in Bapuji Institute of Engineering & Technology, Davangere. The Cell has been functioning actively for Academic and Administrative activities. \"Quality Policy\" is the major objective of our IQAC through continuous monitoring and constant follow-up of undergoing activities. \"The prime task of the IQAC is to develop a system for conscious, consistent and catalytic improvement in the institution's overall performance. For this, IQAC cell will channelize all efforts and measures of the institution towards promoting its holistic academic excellence.\"",
    objectives: [
      "To develop and apply quality benchmarks/parameters for various academic and administrative activities.",
      "To facilitate the creation of a learner-centric environment conducive to quality education and faculty maturation to adopt the required knowledge and technology for participatory teaching and learning process.",
      "To monitor the feedback taken from students, parents and other stakeholders on quality-related institutional processes.",
      "Dissemination of information on various quality parameters of higher education.",
      "Documentation of the various programs/activities leading to quality improvement.",
      "To act as a nodal agency of the Institution for coordinating quality-related activities, including adoption and dissemination of best practices.",
      "Development of Quality Culture in the institution.",
      "Preparation of the Annual Quality Assurance Report (AQAR) as per guidelines and parameters of NAAC, to be submitted to NAAC.",
    ],
    team: [
      "Dr. C P Anilakumar",
      "Dr. Chidananda G",
      "Dr. Nirmala S O",
      "Dr. S R Basavarajappa",
      "Dr. Gururaj T",
      "Dr. Arun Kumar G H",
      "Dr. A B Vinayaka Patil",
      "Dr. G Y Vishwanath (MBA)",
      "DQAC of all deparments",
    ],
  },
  {
    slug: "examination-cell-coe",
    name: "Examination Cell/CoE",
    category: "Academics",
    summary: "Runs the examination system that autonomy brought in-house — scheduling and conducting examinations, managing evaluation, and publishing results across every programme.",
    team: [
      "Dr. Kumarappa S",
      "Dr. Anjaneya L H",
      "Dr. Kiran Kumar G H",
      "Dr. Basavana Gowda S N",
      "Dr. Patil N S",
      "Sri. Vijay K S (MBA)",
    ],
  },
  {
    slug: "iqac-2",
    name: "IQAC",
    category: "Academics",
    summary: "The IQAC coordination pair listed under the Dean (Academics) portfolio, connecting the Dean's academic remit to the quality processes the institute's full cell runs.",
    team: [
      "Dr. C P Anila kumar",
      "Dr. Chidananda G",
    ],
  },
  {
    slug: "strategic-planning-committe-institutional-development-plan-idp-committee",
    name: "Strategic Planning Committe /Institutional Development Plan (IDP) Committee",
    category: "Academics",
    summary: "Prepares the Institutional Development Plan and sets the institute's direction beyond a single academic year — where it intends to grow and what those choices commit departments to.",
    team: [
      "Dr. Sujith Kumar S H (MBA)",
      "Dr. K M Prakash",
      "Dr. Nirmala S O",
    ],
  },
  {
    slug: "staff-selection-and-promotion-committee",
    name: "Staff Selection and Promotion Committee",
    category: "Academics",
    summary: "Assesses the teaching staff each department requires and plans recruitment to the affiliating bodies' norms, and considers promotion for faculty already in service.",
    purpose: "The Institute constituted the Staff Selection Committee to evaluate the requirement of teaching staff for the Institute and plan accordingly to recruit as per the norms laid down by the affiliating bodies. The committee will discharge the functions as listed.",
    objectives: [
      "To act as advisory to management.",
      "To follow the set of rules and quality policy during recruiting process.",
      "To provide long term strategic guidance to improve the quality of faculty, teaching learning process.",
      "To ensure that the advertisements are given for various vacant posts after approval from the management.",
      "To evaluate the suitability of prospective candidates for a particular position.",
      "To ensure that proper procedure is followed during recruitment process.",
      "To seek approval of selection list from management.",
      "To monitor the work culture of faculty and report to management for further decision.",
    ],
    team: [
      "Sri. Siddesh A S",
      "Dr. Ashok K",
      "Dr. Sreenivas B R",
      "Concerned HOD",
    ],
  },
  {
    slug: "dqac-coordinators",
    name: "DQAC Coordinators",
    category: "Academics",
    summary: "Department-level quality coordinators. Each maintains their own department's quality documentation and metrics and feeds them up to the institute's IQAC.",
    team: [
      "All Department Level",
    ],
  },
  {
    slug: "audit-and-feedback-analysis-committee",
    name: "Audit and Feedback Analysis Committee",
    category: "Academics",
    summary: "Audits the institute's academic processes and analyses feedback from students, parents and other stakeholders, turning what they report into findings the institute can act on.",
    team: [
      "IQAC members",
    ],
  },
  {
    slug: "dean-training-placement-and-career-advancement-ug-and-pg",
    name: "Dean : Training Placement and Career Advancement (UG and PG)",
    category: "Training & placement",
    summary: "Leads training, placement and career advancement for UG and PG students across every department, holding together the cells that prepare students for recruitment and after.",
    team: [
      "Dr. Nirmala C R",
    ],
  },
  {
    slug: "training-and-placement-committee",
    name: "Training and Placement Committee",
    category: "Training & placement",
    summary: "Organises the institute's placement activity — employer outreach, campus recruitment drives, and preparing students for the aptitude tests, technical rounds and interviews involved.",
    purpose: "The institute has constituted the Training and Placement Cell, primarily to provide the oversight into the students training and placement activities of the organization. The committee shall discharge the functions as under.",
    objectives: [
      "To motivate students to enhance technical knowledge and soft skills through career planning and goal setting.",
      "To prepare the students to meet the Industries requirement.",
      "To encourage students to aspire for higher studies and to take competitive examinations such as CAT, GATE, TOFEL, GRE, IES, UPSC, KPSC, etc.",
      "Aiming to place the maximum number of students through campus & off-campus interviews.",
      "To organize career development programs, such as; -Personality Development Program, -Communication Skills Program, -Group Discussion Practice, -Entrepreneurship Development Program, -Mock Interview Sessions, -Industry to Institute Convergence expert interaction sessions, -Industry Internship Programs.",
    ],
    team: [
      "Dr. Nirmala C R & Department Coordinators",
    ],
  },
  {
    slug: "career-advancement-cell",
    name: "Career Advancement Cell",
    category: "Training & placement",
    summary: "Guides students towards what follows the degree — higher studies, competitive and civil-services examinations, and career paths beyond a campus placement offer.",
    team: [
      "NPTEL - SPOC — Dr. Bhuvaneshwari K V",
      "VTU Online Course Coordinator — Dr. Santhosh K C",
      "BE Honors Coordinator — Sri. Punith S P",
      "Minors Degree Coordinators — Sri. Punith B H",
      "ABC Committee — Department Coordinators",
    ],
  },
  {
    slug: "edc",
    name: "EDC",
    category: "Training & placement",
    summary: "The Entrepreneurship Development Cell. It builds entrepreneurial thinking among students and supports those who want to start something of their own rather than take a placement.",
    purpose: "The Institute constituted the Entrepreneurship Development Cell for the year 2022-2023 to develop institutional mechanism to create entrepreneurial culture among students and to foster techno entrepreneurship for generation of wealth and employment.",
    objectives: [
      "Motivate the students to participate in all the activities of ED Cell.",
      "Organizing Entrepreneurship Awareness Camps and Entrepreneurship Development Programmes.",
      "Motivate students to choose Entrepreneurship as their career.",
      "Organize business plan competitions in collaboration with industry.",
      "Motivating prospective entrepreneurs to participate in several nationwide entrepreneurial competitions.",
      "Arrange the interaction programmes with diverse entrepreneur and financial organisations.",
      "Guide the aspiring entrepreneurs in preparing project proposals, obtaining project approvals and loans.",
    ],
    team: [
      "Dr. Banumathi K L",
      "Sri. Santhosh T",
      "Dr. Prakash S Alalageri(MBA)",
      "Dr. Y C Mohan (MBA)",
      "Department Coordinators",
    ],
  },
  {
    slug: "internship-training-committee",
    name: "Internship Training Committee",
    category: "Training & placement",
    summary: "Arranges student internships with the institute's industry partners and monitors them while running, so time inside a company counts as structured training.",
    team: [
      "Dr. Gangadharappa S",
      "Dr. Geetha M (MCA)",
      "Dr. Chaitra K S (MBA)",
      "Department Coordinators",
    ],
  },
  {
    slug: "skill-development-cell",
    name: "Skill Development Cell",
    category: "Training & placement",
    summary: "Runs skill-development and value-added programmes alongside the formal curriculum, covering practical competencies a syllabus revised on a multi-year cycle cannot keep pace with.",
    team: [
      "Dr. G Manavendra",
      "Dr. Sharan A S",
      "Dr. N S Manjunath",
      "Department Coordinators",
    ],
  },
  {
    slug: "dean-admission-ug-and-and-pg",
    name: "Dean:Admission(UG and and PG)",
    category: "Admissions",
    summary: "Leads admissions across every undergraduate and postgraduate programme, from counselling through to the point a student is enrolled and assigned to a department.",
    team: [
      "Dr.Leela G H",
    ],
  },
  {
    slug: "admission-committee",
    name: "Admission Committee",
    category: "Admissions",
    summary: "Conducts the admission process at each intake — verifying eligibility, handling counselling and seat allotment, and completing enrolment across the quotas the institute admits under.",
    team: [
      "Dr.Leela G H",
      "Dr.Avinash K G",
      "Dr.Shruthi Kantharaj(MBA)",
      "Department coordinator",
    ],
  },
  {
    slug: "student-induction-program-committee",
    name: "Student Induction Program Committee",
    category: "Admissions",
    summary: "Runs the induction programme that settles incoming students into the institute, introducing them to the campus, their department and what the course expects of them.",
    team: [
      "Dr.N S Basavarajappa and Team",
    ],
  },
  {
    slug: "dean-student-welfare",
    name: "Dean:Student Welfare",
    category: "Student welfare",
    summary: "Leads student welfare across the institute — hostels, counselling, scholarships, cultural and sporting life — and the cells that deliver each of those day to day.",
    purpose: "The Institute Constituted STUDENTS’ WELFARE COUNCIL for the year 2022-23 in order to plan the Various Activities to be Conducted during this year under SWC.",
    objectives: [
      "Nomination/ Election of Office Bearers of Students Welfare Council",
      "Monitor the Facilities Provided to the Students-Medical, Financial",
      "Provide an Environment for Spiritual and Social Growth",
      "Date for Conducting Ethnic Day-23",
      "Fixation of Judges for the Events of Ethnic Day",
      "4. Programme to be scheduled to conduct Cultural Fest 2023(DAVANA-23)",
      "Conduction of Events such as Debate, SKIT, MIME, Group song, Fashion Show etc under the Umbrella of DAVANA",
      "Identification of Judges for the Various Events of the Fest",
      "Identifying the Students to participate in the University Youth Festival",
      "Valedictory/ Closing Function of Students Welfare Council",
      "About us",
      "Contact us",
      "Privacy policy",
      "Terms & Conditions",
    ],
    team: [
      "Dr.Vinutha H P",
      "Smt.Bhagya S",
      "Dr.Shivakumaraswamy G M",
      "Sri Satish D Raikar(MBA)",
      "Advisors,Conveners and Coordinator on rotation basis",
    ],
  },
  {
    slug: "anti-ragging-committee",
    name: "Anti–Ragging Committee",
    category: "Student welfare",
    summary: "Oversees the complete elimination of ragging from the campus and hostels, with a squad attached for enforcement, discharging the statutory duty to keep incoming students safe.",
    purpose: "The institute has constituted the Anti-Ragging and Squad Committee to provide the oversight into the complete wipe out of the menace from the campus/ hostels of the Institution.",
    objectives: [
      "To arrange for collecting the online affidavit from the students stating that he/she will not indulge in any act of ragging.",
      "To arrange for the display of posters and display boards related to anti ragging in the college campus at prominent places.",
      "To make surprise visits to girls’ and boys’ common rooms, hostels and other places and to conduct spot enquiry on incidents of ragging if any.",
      "To resolve the complaints received related to ragging in time.",
      "To always maintain alert vigil and ensure that the anti-ragging squad of the Institution carry out its functions.",
    ],
    team: [
      "Dr.Ashwini M Rao",
      "Dr.T N Vijay Kumar and team",
    ],
  },
  {
    slug: "grievance-redressal-committee",
    name: "Grievance Redressal Committee",
    category: "Student welfare",
    summary: "Receives grievances from students and staff and works them to resolution, acting as the institute's ombudsman so a complaint does not depend on the person complained about.",
    purpose: "The institute has constituted the OMBUDSMAN/Grievance Redressal Committee, primarily to provide the oversight into the grievances of faculty and students of the Institution.",
    objectives: [
      "To uphold the dignity of the Institution by ensuring trouble-free atmosphere in the campus by promoting cordial student-student relationship and student-faculty relationship.",
      "To encourage the staff/students to express their grievances / problems freely and frankly, without any fear of being victimized",
      "To advise staff/students of the Institution to respect the right and dignity of one another and show utmost restraint and patience whenever any occasion of rift arises.",
      "To ensure that the students or staff to refrain from inciting students or staff against other co-students, faculties or colleagues or college administration.",
      "To advise all staffs to be affectionate to the students and not to behave in a vindictive manner towards any of them for any reason.",
      "To attend the cases promptly on receipt of written grievances from the students/staff.",
      "Shall coordinate, monitor and ensure the ‘Redressal of grievance’ within a reasonable time limit depending upon the gravity of the grievance",
      "To take formally the action on all the cases and acts in accordance with the laid down Policy of the Institution.",
      "The Committee submits ‘findings’ of each case attended to the authority for furtherance in the matter.",
    ],
    team: [
      "Prof.M E Ashtalatha and team",
    ],
  },
  {
    slug: "cicc",
    name: "CICC",
    category: "Student welfare",
    summary: "The College Internal Complaint Committee. It oversees grievances raised by women employees and girl students, including sexual harassment, and investigates them in confidence.",
    purpose: "The institute has constituted the College Internal Complaint Committee (CICC), to provide the oversight into the grievances of women employees and girl students of the institution.",
    objectives: [
      "To ensure safe working and learning conditions for women employees and girl students in the campus.",
      "To interact with women employees and girl students at regular intervals and to collect the feedback on working and learning conditions in the campus.",
      "To investigate the facts quickly and confidentially related to the complaint.",
      "To resolve the issues related to women employees and girl students’ sexual harassment.",
      "To decide the punitive actions to be taken on guilty in consultation with the management.",
    ],
    team: [
      "Smt.Geetha V K and team",
    ],
  },
  {
    slug: "sc-st-cell",
    name: "SC/ST Cell",
    category: "Student welfare",
    summary: "Looks after the welfare of SC, ST and OBC students and their grievances, raises awareness of government initiatives, and helps them reach the benefits those schemes carry.",
    objectives: [
      "To look after welfare and addressing grievances of SC, ST & OBC students of the institute.",
      "To create awareness among students about various governmental initiatives on academics.",
      "To provide necessary support for vertical progression.",
      "To implement and optimally utilize the benefits of the schemes offered by the State Government, Government of India, UGC and AICTE.",
    ],
    team: [
      "Smt.Vaudeva Nayaka B L and team",
    ],
  },
  {
    slug: "hostel-wardens",
    name: "Hostel Wardens",
    category: "Student welfare",
    summary: "The wardens responsible for the day-to-day running of the institute's hostels — accommodation, discipline and the welfare of the students living on campus.",
    team: [
      "Dr.Manavendra G and team",
      "Dr.Chandrashekhar S M and team",
      "Dr.Anitha G and team",
    ],
  },
  {
    slug: "cultural-committee",
    name: "Cultural Committee",
    category: "Student welfare",
    summary: "Plans and runs the institute's cultural life through the year — the annual festival, inter-collegiate events and the smaller performances held between them.",
    team: [
      "Dr.Chandrashekhar S M",
      "Sri.Vinay M T",
      "Sri.Sreenidhi Kulkarni",
      "Sri.Chandrashekhar M V",
      "Department Coordinators",
    ],
  },
  {
    slug: "sports-pe-coordinator",
    name: "Sports/PE Coordinator",
    category: "Student welfare",
    summary: "Coordinates physical education across the institute — the sports facilities, the teams representing it at inter-collegiate level, and routine activity for everyone else.",
    team: [
      "Dr.C M Kalleshappa",
      "Dr.Naseer R",
      "Dr.Vinay Parol",
      "Sri.Amarnath P C",
      "Department Coordinators",
    ],
  },
  {
    slug: "yoga-coordinator",
    name: "Yoga Coordinator",
    category: "Student welfare",
    summary: "Runs yoga and wellness sessions for students and staff, giving the institute's wellbeing provision a strand that does not depend on competitive sport.",
    team: [
      "Smt Geetha V K",
      "Smt.Madhu N Hiremath",
      "Department Coordinators",
    ],
  },
  {
    slug: "graduation-day-function-committe",
    name: "Graduation Day Function Committe",
    category: "Student welfare",
    summary: "Plans and conducts the annual graduation ceremony — the arrangements, the programme and the conferment itself, the last formal occasion of a student's time here.",
    team: [
      "On rotation basis",
    ],
  },
  {
    slug: "scholarship-coordinator",
    name: "Scholarship Coordinator",
    category: "Student welfare",
    summary: "Helps students find and apply for the scholarships open to them, government and institutional, and follows applications through sanction so eligibility becomes money received.",
    team: [
      "Dr.Ashok K",
      "Dr.Avinash K G",
      "Department Coordinators",
    ],
  },
  {
    slug: "counselling-cell",
    name: "Counselling Cell",
    category: "Student welfare",
    summary: "Provides confidential counselling on academic and personal difficulties, so a student who is struggling has a route to support outside their own department.",
    team: [
      "Dr.Rekha C",
      "Department Coordinators",
    ],
  },
  {
    slug: "parents-meeting-coordinator",
    name: "Parents Meeting Coordinator",
    category: "Student welfare",
    summary: "Organises parent-teacher meetings and keeps families informed of progress, so difficulties reach home while there is still a semester left to act on them.",
    team: [
      "Through Mentors",
    ],
  },
  {
    slug: "club-coordinator",
    name: "Club Coordinator",
    category: "Student welfare",
    summary: "Oversees the institute's student clubs and the calendar they run between them, keeping technical, cultural and interest-based societies active through the year.",
    team: [
      "Smt Radhika Priya Y R",
      "Various club at deparment level",
    ],
  },
  {
    slug: "dean-infrastructure-planning-and-maintenance",
    name: "Dean:Infrastructure Planning and Maintenance",
    category: "Infrastructure",
    summary: "Leads planning, development and upkeep of the institute's physical estate — buildings, laboratories and facilities — and the engineering and maintenance functions behind them.",
    team: [
      "Dr.S B Mallikarjun",
      "Dr.S Suresh",
      "Sri. R S Chikkanagoudar",
    ],
  },
  {
    slug: "resident-engineer",
    name: "Resident Engineer",
    category: "Infrastructure",
    summary: "Supervises construction and civil works on the campus, holding the institute's side of building projects on site — quality, progress and the decisions taken there.",
    team: [
      "Sri.Ravi Kumar",
    ],
  },
  {
    slug: "maintenance-department",
    name: "Maintenance Department",
    category: "Infrastructure",
    summary: "Maintains the campus fabric — buildings, fittings, plumbing, furniture and the routine repairs that keep classrooms, laboratories and hostels usable through the year.",
    team: [
      "Sri.M C Patel",
    ],
  },
  {
    slug: "security-cell",
    name: "Security Cell",
    category: "Infrastructure",
    summary: "Manages campus security — access control at the gates, patrolling, and the safety of people and property across a site occupied well beyond teaching hours.",
    team: [
      "Sri.Nagaraj H(Office)",
    ],
  },
  {
    slug: "canteen",
    name: "Canteen",
    category: "Infrastructure",
    summary: "Oversees the campus canteen — its hygiene, the quality of what it serves and the prices it charges — for hostel residents and day scholars alike across the teaching day.",
    team: [
      "Dr.S Suresh",
      "Sri.R S Chikkanagoudar",
    ],
  },
  {
    slug: "library",
    name: "Library",
    category: "Infrastructure",
    summary: "Manages the central library — the collection, journal and database subscriptions, reading facilities and lending — and the access arrangements around them.",
    team: [
      "Dr.K V Manjunath and Team",
    ],
  },
  {
    slug: "cooperative-society",
    name: "Cooperative Society",
    category: "Infrastructure",
    summary: "Runs the staff cooperative society and the services it provides members, an employee-owned body operating alongside the institute's administration rather than within it.",
    team: [
      "Dr.Shankara Gowda B B(MCA)",
      "Sri.Vinay Patil(MCA) and Team",
    ],
  },
  {
    slug: "green-and-energy-audit-committee",
    name: "Green and Energy Audit Committee",
    category: "Infrastructure",
    summary: "Carries out the institute's green and energy audits and drives the measures that follow, covering energy use, water, waste and the campus environment more broadly.",
    team: [
      "Dr.Veena Kumar Adi",
      "Dr.B Suresh",
      "Dr.Manjunath H M",
      "Sri.Ashok E",
      "Sri.Vagish M",
      "Sri.Praveen Kumar G B",
    ],
  },
  {
    slug: "electrical-maintenance",
    name: "Electrical Maintenance",
    category: "Infrastructure",
    summary: "Maintains the campus electrical installations and power supply — distribution, backup and the fittings in every block — on which laboratories and examinations depend.",
    team: [
      "Sri.Karibasavaraju T S",
    ],
  },
  {
    slug: "auditorium-i-c",
    name: "Auditorium I/C",
    category: "Infrastructure",
    summary: "Manages the institute's auditorium and its scheduling, allocating the venue between ceremonies, conferences, cultural programmes and department functions.",
    team: [
      "Sri. Siddesh A S and Team",
    ],
  },
  {
    slug: "dean-research-and-department",
    name: "Dean:Research and Department",
    category: "Research",
    summary: "Leads research and development across the institute — funded projects, publications and the recognised research centres — built on top of an undergraduate teaching base.",
    team: [
      "Dr.A G Shankaramurthy",
    ],
  },
  {
    slug: "research-committee",
    name: "Research Committee",
    category: "Research",
    summary: "Promotes research across departments, reviews proposals before they go forward, and supports faculty and student research through funding, approval and reporting.",
    purpose: "The institute has constituted the Research Advisory Committee, primarily to provide the oversight into the research activities of faculty and students of the organization. The committee shall discharge the functions as under",
    objectives: [
      "To arrange workshop related to Research methodology to create awareness among faculty.",
      "To assist departments in organizing National and International conferences/seminars.",
      "To motivate the faculty to write research proposals to the various funding agencies.",
      "To monitor the scheme of Institutional grants for best student projects of every department, which has been introduced from 2015-16.",
      "To advise to allocate the funds for research centers for publications, proposal writing etc.",
      "Scrutiny of the research proposals submitted by faculty/student.",
      "Monitor the progress of projects, procurement, account, utilization certificate etc.",
    ],
    team: [
      "Dr.Shankaramurthy",
      "Dr.N S Manjunath",
      "Dr.Narash Patil K M",
      "Dr.Chaitra K S(MBA)",
    ],
  },
  {
    slug: "iic",
    name: "IIC",
    category: "Research",
    summary: "The Institution's Innovation Council, constituted under the national IIC programme. It runs the innovation activities that programme prescribes and reports participation back.",
    team: [
      "Dr.Vinutha H P",
      "Dr.Santosh K C",
      "Smt.Sushma C",
      "Department Coordinators",
    ],
  },
  {
    slug: "incubation-center",
    name: "Incubation Center",
    category: "Research",
    summary: "Hosts and supports early-stage ventures started by students and faculty — space, guidance and backing in the gap between a final-year project and an actual company.",
    team: [
      "Dr.Poornima B",
      "Dr.Sharan A S",
      "Dr.N S Manjunath",
      "Dr.Amjad Khan",
    ],
  },
  {
    slug: "dean-public-relations",
    name: "Dean:Public Relations",
    category: "Public relations",
    summary: "Leads the institute's public relations and external communication — how it presents itself to prospective students, to the press and to the wider region.",
    team: [
      "Dr.C M Kalleshappa",
      "Dr.G P Desai",
    ],
  },
  {
    slug: "advertisement-committee",
    name: "Advertisement Committee",
    category: "Public relations",
    summary: "Plans the institute's advertising, particularly through the admission season when prospective students and their families are choosing between colleges.",
    team: [
      "Dr.Kalleshappa C M",
      "Dr.G P Desai",
      "Sri.Vinay M T",
      "Smt.Vanisha D Patel(MBA)",
    ],
  },
  {
    slug: "magazine-committee",
    name: "Magazine Committee",
    category: "Public relations",
    summary: "Produces the institute magazine — gathering contributions from students and staff, editing them and seeing the issue to print as its own record of the year.",
    team: [
      "On Rotation Basis",
      "Department Coordinators",
    ],
  },
  {
    slug: "social-media-committee",
    name: "Social Media Committee",
    category: "Public relations",
    summary: "Runs the institute's social media presence and online communication, reaching current students, recent graduates and prospective applicants where they already are.",
    team: [
      "Website Coordinators",
    ],
  },
  {
    slug: "community-engagement-and-social-responsibility-committee",
    name: "Community Engagement and Social Responsibility Committee",
    category: "Outreach",
    summary: "Plans the institute's community outreach and social responsibility work — the activity that takes students and staff off the campus and into the surrounding region.",
    team: [
      "Dr.Shankara Gowda B B(MCA)",
      "Dr.Santosh Kumar M V",
    ],
  },
  {
    slug: "ncc-cell",
    name: "NCC Cell",
    category: "Outreach",
    summary: "Runs the institute's National Cadet Corps unit — drill, training and the annual camps — for students taking the NCC route and the certification it carries.",
    team: [
      "Dr.B M Prasanna",
      "Department Coordinators",
    ],
  },
  {
    slug: "nss-committee",
    name: "NSS Committee",
    category: "Outreach",
    summary: "Runs the institute's National Service Scheme unit and its community-service programmes, including the residential village camps the scheme is built around.",
    team: [
      "Dr.Krishna Kumar T K",
      "Department Coordinators",
    ],
  },
  {
    slug: "youth-redcross-cell",
    name: "Youth Redcross Cell",
    category: "Outreach",
    summary: "Runs Youth Red Cross activity at the institute — blood donation drives, health camps and awareness programmes — alongside students' engineering studies.",
    team: [
      "Dr.Chandrashekhar S M",
      "Sri.Prasanth Kumar H K",
      "Department Coordinators",
    ],
  },
  {
    slug: "aicte-activity-point-program-coordinator",
    name: "AICTE Activity Point Program Coordinator",
    category: "Outreach",
    summary: "Tracks and certifies the activity points AICTE requires every student to earn outside the classroom before graduating, and verifies the record behind them.",
    team: [
      "Dr.Anil Kumar K N",
      "Dr.Vikas K",
      "Dr.Punith Gowda R J",
      "Department Coordinators",
    ],
  },
  {
    slug: "uhv-coordinator",
    name: "UHV Coordinator",
    category: "Outreach",
    summary: "Coordinates the Universal Human Values programme prescribed for engineering students — the strand concerned with ethics and conduct rather than technical competence.",
    team: [
      "Dr.Naresh Patel K M",
      "Sri.Umesh D E",
      "Department Coordinators",
    ],
  },
  {
    slug: "affiliation-and-accriditation-committee",
    name: "Affiliation and Accriditation Committee",
    category: "Accreditation",
    summary: "Coordinates the institute's affiliation and accreditation work with the affiliating university and the national regulatory bodies, each running its own cycle.",
    team: [
      "Dr.Sunitha G S",
      "Dean(Academics)",
      "IQAC",
    ],
  },
  {
    slug: "vtu-lic",
    name: "VTU-LIC",
    category: "Accreditation",
    summary: "Handles the VTU Local Inquiry Committee process — the inspection on which affiliation to Visvesvaraya Technological University depends, run on the university's schedule.",
    team: [
      "Dr.Anjaneya L H",
      "Dr.Kiran Kumar G H",
      "Dr.Prakash K M",
      "Dr.N S Patil",
      "Dr.Varsh M",
      "Dr.Naveen Kumar H M",
      "Departmeent Coordinators(DQAC)",
    ],
  },
  {
    slug: "aicte-and-ugc",
    name: "AICTE and UGC",
    category: "Accreditation",
    summary: "Manages AICTE approval and UGC recognition — the applications, annual returns and compliance each requires — which set what the institute may offer and admit to.",
    team: [
      "Dr.Nirmala C R",
      "Dr.Naveen Kumar H M",
      "Sri.Raghu B R",
    ],
  },
  {
    slug: "naac",
    name: "NAAC",
    category: "Accreditation",
    summary: "Prepares and coordinates the institute's NAAC assessment — the self-study report, the evidence behind it and the peer-team visit that together produce its grade.",
    team: [
      "Dr.Arun Kumar G H",
      "Dr.Bhuvaneshwari K V",
      "Dr.S R Basavarajappa",
      "Dr.Naveen Kumar K R",
      "Dr.Chaitra K S(MBA)",
      "Head of P,C and M",
      "Department Coordinators(DQAC)",
    ],
  },
  {
    slug: "nba",
    name: "NBA",
    category: "Accreditation",
    summary: "Prepares and coordinates NBA accreditation, granted programme by programme rather than institute-wide, turning on outcome-based education and departmental evidence.",
    team: [
      "Sri.Umesh B S",
      "Dr.Sreenivas B R",
      "Smt.Deepashree K M",
      "Department Coordinators(DQAC)",
    ],
  },
  {
    slug: "nirf",
    name: "NIRF",
    category: "Accreditation",
    summary: "Compiles and submits the institute's annual NIRF return — teaching resources, research output, graduation outcomes and perception — on which it is ranked each year.",
    team: [
      "Dr.C M Kalleshappa",
      "Dr.D G Praveen Kumar",
      "Dr.G Y Vishwanath(MBA)",
      "Department Coordinators(DQAC)",
    ],
  },
  {
    slug: "aishe",
    name: "AISHE",
    category: "Accreditation",
    summary: "Compiles and submits the institute's annual AISHE return, the All India Survey on Higher Education, which collects enrolment, staffing and infrastructure data.",
    team: [
      "Dr.Chetana Prakash",
      "Dr.Sreenivas B R",
      "Dr.Rekha C",
      "Dr.Gangharappa S",
      "Smt.Suma K G",
      "Department Coordinators(DQAC)",
    ],
  },
  {
    slug: "alumni-association",
    name: "Alumni Association",
    category: "Alumni",
    summary: "The institute's alumni body, keeping graduates connected to one another and to the campus. Its office-bearers and roster are listed on the Alumni page.",
    team: [],
  },
  {
    slug: "alumni-coordinator",
    name: "Alumni coordinator",
    category: "Alumni",
    summary: "The department alumni coordinators. Each keeps their department's alumni contact details current and runs its meets, which is how the network stays reachable.",
    team: [
      "Dr.K C Devendrappa",
      "Sri Sheik Imran",
      "Department coordinator",
    ],
  },
  {
    slug: "erp-management-committee",
    name: "ERP Management Committee",
    category: "Administration",
    summary: "Oversees the institute's ERP system and the modules running on it — admissions, attendance, examinations and student records — and the integrity of their data.",
    team: [
      "IQAC",
      "Dr.Anitha G",
      "Dr.Varsha M",
      "Dr.Naseer R",
      "Dr.Geetha (MCA)",
      "Dr.Anusha R",
      "Department Coordinators",
    ],
  },
  {
    slug: "website-and-internet-maintenance-committee",
    name: "Website and Internet Maintenance Committee",
    category: "Administration",
    summary: "Maintains the institute website and the campus internet services — the first being how most people outside encounter it, the second what those inside depend on.",
    team: [
      "Dr.Naveen Kumar K R",
      "Dr.Abdul Razak M S",
      "Sri.Vishwanath V K",
      "Department Coordinators",
    ],
  },
  {
    slug: "first-year-academics-coordinators",
    name: "First Year Academics Coordinators",
    category: "Administration",
    summary: "Coordinate teaching and student support through the first year — the common syllabus every branch shares before specialisation, and the transition from school.",
    team: [
      "Heads of P,C,M",
    ],
  },
];
